import Button from '@jetbrains/ring-ui/components/button/button'
import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import Link from '@jetbrains/ring-ui/components/link/link'
import Radio from '@jetbrains/ring-ui/components/radio/radio'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/src/query/fetchBaseQuery'
import React from 'react'

import httpsConfigurationApi from '../../HttpsConfigurationPage.api'
import {
  useAgentsParamsLink,
  useDisplayErrorAlert,
  useServerUrlInfo,
} from '../../HttpsConfigurationPage.hooks'
import type {Connector} from '../../HttpsConfigurationPage.types'
import {RedirectStatus} from '../../HttpsConfigurationPage.types'
import {usePortConfiguration} from '../../PortConfiguration/PortConfiguration'

import Message, {MessageType} from './Message/Message'

import styles from './ConnectorSettings.css'

import {resolveRelative} from 'src/utils/url'

enum Protocol {
  HTTP = 'http:',
  HTTPS = 'https:',
}

function GlobalSettingsLink({children}: {children: React.ReactNode}) {
  return (
    <Link
      href={resolveRelative('/admin/admin.html?item=serverConfigGeneral')}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </Link>
  )
}

type Props = Connector
function ConnectorSettings({redirectStatus, port: defaultPort}: Props) {
  const [currentStrategy, setCurrentStrategy] = React.useState(redirectStatus)
  const [prevStrategy, setPrevStrategy] = React.useState(redirectStatus)
  const [prevPort, setPrevPort] = React.useState(String(defaultPort))

  const [configureStrategy, {isLoading: strategyLoading, error: strategyError}] =
    httpsConfigurationApi.endpoints.setRedirectStrategy.useMutation()
  const [configurePort, {isLoading: portLoading, error: portError}] =
    httpsConfigurationApi.endpoints.setPort.useMutation()

  const {isRunningInContainer} = httpsConfigurationApi.endpoints.getDefaultHttpsPort.useQuery(
    undefined,
    {selectFromResult: ({data}) => ({isRunningInContainer: data === '8443'})},
  )
  const {hostname: serverHostname, origin} = useServerUrlInfo()
  const redirectURL = origin ?? 'the server URL'

  const agentsParamsLink = useAgentsParamsLink('teamcity.serverUrl')
  const {validate, PortConfiguration, portConfigurationProps} = usePortConfiguration(
    String(defaultPort),
  )
  const {value: currentPort} = portConfigurationProps

  const isStrategyChanged = currentStrategy !== prevStrategy
  const isPortChanged = currentPort.length !== 0 && currentPort !== prevPort

  const onChangeStatus = React.useCallback((value: string) => {
    setCurrentStrategy(value as RedirectStatus)
  }, [])

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!validate()) {
        return
      }

      const requests = []
      const currentURL = new URL(location.href)

      if (isStrategyChanged) {
        requests.push(
          configureStrategy({redirectStatus: currentStrategy})
            .unwrap()
            .then(() => {
              setPrevStrategy(currentStrategy)
            }),
        )
      }

      if (isPortChanged) {
        requests.push(
          configurePort({port: currentPort})
            .unwrap()
            .then(() => {
              setPrevPort(currentPort)
            }),
        )
      }

      Promise.allSettled(requests).then(results => {
        if (serverHostname == null || isRunningInContainer) {
          return
        }
        const strategyUpdateResult = isStrategyChanged ? results[0] : null
        const portUpdateResult = isPortChanged ? results[isStrategyChanged ? 1 : 0] : null

        // We assume that losing the server connection right after making a port update request
        // indicates a successful result since changing the port makes the server unreachable using
        // the current address.
        const isServerConnectionLost =
          portUpdateResult?.status === 'rejected' &&
          portUpdateResult?.reason?.status === 'FETCH_ERROR'
        const isStrategyUpdateSuccessful = strategyUpdateResult?.status === 'fulfilled'
        const isPortUpdateSuccessful =
          portUpdateResult?.status === 'fulfilled' || isServerConnectionLost

        if (
          (isStrategyUpdateSuccessful &&
            currentURL.protocol === Protocol.HTTP &&
            currentStrategy !== RedirectStatus.DISABLED) ||
          (currentURL.protocol === Protocol.HTTPS && isPortUpdateSuccessful)
        ) {
          currentURL.protocol = Protocol.HTTPS
          currentURL.hostname = serverHostname
          currentURL.port = isPortUpdateSuccessful ? currentPort : prevPort
          location.href = currentURL.toString()
        }
      })
      ;(document.activeElement as HTMLElement)?.blur()
    },
    [
      validate,
      currentPort,
      prevPort,
      serverHostname,
      currentStrategy,
      isPortChanged,
      isStrategyChanged,
      isRunningInContainer,
      configurePort,
      configureStrategy,
    ],
  )

  const warningMessage = redirectURL.startsWith(Protocol.HTTP) ? (
    <Message className={styles.redirectMessage} type={MessageType.WARNING}>
      <div>
        {'The server URL is still using '}
        <strong>{'HTTP'}</strong>
        {'. To ensure redirects function correctly, go to '}
        <GlobalSettingsLink>{'global settings'}</GlobalSettingsLink>
        {' and change the server URL to '}
        <strong>{'HTTPS'}</strong>
        {'.'}
      </div>
    </Message>
  ) : null

  const changeRedirectUrlBlock = (
    <div>
      {'You can change the redirect URL by modifying the server URL setting on the '}
      <GlobalSettingsLink>{'Global Settings page'}</GlobalSettingsLink>
      {'.'}
    </div>
  )

  useDisplayErrorAlert(
    (strategyError as FetchBaseQueryError)?.status !== 'FETCH_ERROR' ? strategyError : null,
  )
  useDisplayErrorAlert(
    (portError as FetchBaseQueryError)?.status !== 'FETCH_ERROR' ? portError : null,
  )

  return (
    <form onSubmit={handleSubmit}>
      <PortConfiguration className={styles.portConfig} {...portConfigurationProps} />
      <H2 className={styles.redirectTitle}>{'HTTPS Redirect'}</H2>
      <Radio value={currentStrategy} onChange={onChangeStatus}>
        <Radio.Item value={RedirectStatus.DISABLED}>{'Disabled'}</Radio.Item>
        <div className={styles.description}>{'All clients can use both HTTP and HTTPS.'}</div>
        <Radio.Item value={RedirectStatus.BROWSER_ONLY}>{'Only browser requests'}</Radio.Item>
        <div className={styles.description}>
          {
            'Requests from agents and custom scripts won’t be redirected. Suitable for transition period.'
          }
        </div>
        {isStrategyChanged && currentStrategy === RedirectStatus.BROWSER_ONLY && (
          <Message className={styles.redirectMessage}>
            <div>
              {'All TeamCity browser clients will be redirected to '}
              <strong>{redirectURL}</strong>
              {'.'}
            </div>
            {changeRedirectUrlBlock}
          </Message>
        )}
        {currentStrategy === RedirectStatus.BROWSER_ONLY && warningMessage}
        <Radio.Item value={RedirectStatus.ENABLED}>{'Enable for all requests'}</Radio.Item>
        <div className={styles.description}>
          {
            'Requests from agents and custom scripts will be redirected. Ensure that all clients have updated the server URL with the HTTPS protocol.'
          }
        </div>
        {isStrategyChanged && currentStrategy === RedirectStatus.ENABLED && (
          <Message className={styles.redirectMessage}>
            <div>
              {'All TeamCity clients will be redirected to '}
              <strong>{`${redirectURL}`}</strong>
              {'.'}
            </div>
            {changeRedirectUrlBlock}
            <div className={styles.updateWarning}>
              {'Make sure that you have updated the URL to HTTPS on '}
              <strong>{'all agents'}</strong>
              {'; otherwise the agents using HTTP may not be able to connect to the server.'}
            </div>
            <div>
              {'See the URLs used by the agents to connect to the TeamCity server '}
              <Link href={agentsParamsLink} target="_blank" rel="noreferrer">
                {'on this page'}
              </Link>
              {'.'}
            </div>
          </Message>
        )}
        {currentStrategy === RedirectStatus.ENABLED && warningMessage}
      </Radio>
      <Button
        className={styles.save}
        loader={strategyLoading || portLoading}
        type={'submit'}
        disabled={!isStrategyChanged && !isPortChanged}
        primary
      >
        {'Save'}
      </Button>
    </form>
  )
}

export default React.memo<Props>(ConnectorSettings)
